import React, { useState, useMemo, useCallback, useRef } from 'react';
import { ChevronDown, Download, AlertCircle } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { buildSubdomainUrl } from '../../../utils/domain';
import { buildAvailableForms } from '../../../utils/formDisplayNames';

/**
 * Compute the public URL for a given form/page selection.
 * @param {Object} selected - The selected form item
 * @param {string} customDomain - Portfolio custom domain (subdomain or full domain)
 * @param {string} portfolioId - Portfolio document ID (fallback for direct link)
 * @returns {string|null} - The resolved public URL, or null if not available
 */
function resolvePublicUrl(selected, customDomain, portfolioId) {
  if (!selected) return null;

  if (selected.type === 'portfolio') {
    if (customDomain) {
      return buildSubdomainUrl(customDomain);
    }
    // Fallback: app-domain direct link
    if (portfolioId) {
      return `${window.location.origin}/public/${portfolioId}`;
    }
    return null;
  }

  if (selected.type === 'property') {
    if (customDomain) {
      const base = buildSubdomainUrl(customDomain);
      return `${base}/property/${selected.propertyId}`;
    }
    // Without a custom domain, property pages aren't directly accessible
    // Fall back to app-domain portfolio link (closest we can offer)
    if (portfolioId) {
      return `${window.location.origin}/public/${portfolioId}`;
    }
    return null;
  }

  return null;
}

const QRPreview = ({ variant = 'desktop', propertyTab, portfolio }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [fgColor, setFgColor] = useState('#1c1917'); // stone-900
  const qrRef = useRef(null);

  const customDomain = portfolio?.data?.customDomain || '';
  const portfolioId = portfolio?.docId || '';

  // Build list of available forms — shared utility ensures names match Automations, Responses, etc.
  const availableForms = useMemo(
    () => buildAvailableForms(portfolio, propertyTab?.properties),
    [portfolio, propertyTab?.properties],
  );

  // Auto-select first form if nothing selected
  const selected = useMemo(() => {
    if (selectedId) return availableForms.find(f => f.id === selectedId) || availableForms[0];
    return availableForms[0] || null;
  }, [selectedId, availableForms]);

  // Resolve the target URL
  const targetUrl = useMemo(
    () => resolvePublicUrl(selected, customDomain, portfolioId),
    [selected, customDomain, portfolioId],
  );

  const handleSelectionChange = useCallback((e) => {
    setSelectedId(e.target.value);
  }, []);

  // Download QR as PNG
  const handleDownloadPNG = useCallback(() => {
    const svgElement = qrRef.current?.querySelector('svg');
    if (!svgElement) return;

    const canvas = document.createElement('canvas');
    const size = 1024;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);

      const link = document.createElement('a');
      link.download = `qr-${selected?.name || 'code'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = url;
  }, [selected]);

  // Render the QR code box (shared between layouts)
  const renderQRCode = (qrSize) => {
    if (!targetUrl) {
      return (
        <div className="flex flex-col items-center justify-center text-center p-4" style={{ width: qrSize, height: qrSize }}>
          <AlertCircle className="w-8 h-8 text-stone-300 mb-2" />
          <p className="text-xs text-stone-400">
            {!customDomain && !portfolioId
              ? 'Set up your portfolio to generate QR codes.'
              : 'Select a page above to generate a QR code.'}
          </p>
        </div>
      );
    }

    return (
      <div ref={qrRef}>
        <QRCodeSVG
          value={targetUrl}
          size={qrSize}
          bgColor="#ffffff"
          fgColor={fgColor}
          level="H"
          includeMargin={false}
        />
      </div>
    );
  };

  // ── Mobile Layout ──
  if (variant === 'mobile') {
    return (
      <div className="space-y-6">
        <div className="bg-white border border-notion-border rounded-xl p-4 shadow-card space-y-4">
          <h3 className="text-sm font-semibold text-notion-text">Generator Settings</h3>

          <div>
            <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-1.5">Target URL</label>
            <div className="relative">
              <select
                value={selected?.id || ''}
                onChange={handleSelectionChange}
                className="w-full appearance-none bg-notion-sidebar border-transparent rounded-lg px-3 py-2.5 text-xs font-medium outline-none"
              >
                {availableForms.map(form => (
                  <option key={form.id} value={form.id}>{form.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-stone-400 pointer-events-none" />
            </div>
            {targetUrl && (
              <p className="mt-1.5 text-[10px] text-stone-400 truncate">{targetUrl}</p>
            )}
          </div>

          <div>
            <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">Style</label>
            <div className="flex items-center justify-between">
              <div className="flex gap-3">
                <button
                  onClick={() => setFgColor('#1c1917')}
                  className={`w-8 h-8 rounded-full bg-stone-900 cursor-pointer ${fgColor === '#1c1917' ? 'ring-2 ring-offset-2 ring-stone-300' : 'opacity-50'}`}
                />
                <button
                  onClick={() => setFgColor('#2563eb')}
                  className={`w-8 h-8 rounded-full bg-blue-600 cursor-pointer ${fgColor === '#2563eb' ? 'ring-2 ring-offset-2 ring-blue-300' : 'opacity-50'}`}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-notion-border rounded-2xl p-6 flex flex-col items-center justify-center shadow-lg">
          {renderQRCode(160)}
          <div className="flex gap-2 w-full mt-6">
            <button
              onClick={handleDownloadPNG}
              disabled={!targetUrl}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 border border-notion-border rounded-lg text-xs font-medium text-stone-600 hover:bg-notion-sidebar disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download className="w-3.5 h-3.5" /> PNG
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Desktop Layout ──
  return (
    <div className="max-w-4xl mx-auto h-[calc(100vh-200px)] flex items-center">
      <div className="grid grid-cols-2 gap-12 w-full">
        <div className="space-y-8">
          <div>
            <h2 className="text-xl font-semibold text-notion-text">QR Asset Generator</h2>
            <p className="text-xs text-stone-400 mt-1">Create codes for yard signs and flyers.</p>
          </div>
          <div className="space-y-5">
            <div>
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">Target</label>
              <div className="relative">
                <select
                  value={selected?.id || ''}
                  onChange={handleSelectionChange}
                  className="w-full appearance-none bg-white border border-notion-border rounded-lg px-4 py-2.5 text-xs font-medium outline-none focus:border-stone-400 shadow-sm"
                >
                  {availableForms.map(form => (
                    <option key={form.id} value={form.id}>{form.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-stone-400 pointer-events-none" />
              </div>
              {targetUrl && (
                <p className="mt-2 text-[10px] text-stone-400 truncate">{targetUrl}</p>
              )}
            </div>
            <div>
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-3">Style</label>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setFgColor('#1c1917')}
                  className={`w-6 h-6 rounded-full bg-stone-900 cursor-pointer ${fgColor === '#1c1917' ? 'ring-2 ring-offset-2 ring-stone-300' : 'hover:opacity-80'}`}
                />
                <button
                  onClick={() => setFgColor('#2563eb')}
                  className={`w-6 h-6 rounded-full bg-blue-600 cursor-pointer ${fgColor === '#2563eb' ? 'ring-2 ring-offset-2 ring-blue-300' : 'hover:opacity-80'}`}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white border border-notion-border rounded-2xl p-10 flex flex-col items-center justify-center shadow-lg">
          {renderQRCode(192)}
          <div className="flex gap-2 w-full mt-6">
            <button
              onClick={handleDownloadPNG}
              disabled={!targetUrl}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 border border-notion-border rounded-lg text-xs font-medium text-stone-600 hover:bg-notion-sidebar disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download className="w-3.5 h-3.5" /> Download PNG
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRPreview;
